import React from "react";
import SEO from "./SEO";

const About: React.FC = () => {
  const missionValues = [
    {
      title: "Safety First",
      description:
        "Your safety and the safety of our horses is our top priority. All our rides are guided by experienced staff and we provide proper safety equipment.",
      icon: (
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ),
    },
    {
      title: "Education",
      description:
        "We believe in educating our visitors about horses, nature, and the importance of respecting these magnificent animals.",
      icon: (
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      title: "Fun & Memories",
      description:
        "We create unforgettable experiences that bring joy to families and create lasting memories that will be cherished for years to come.",
      icon: (
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const facilities = [
    {
      title: "Beautiful Setting",
      description:
        "Located in Pretoria East, Boschkop, our stables are surrounded by beautiful countryside and scenic trails perfect for horse riding adventures.",
    },
    {
      title: "Well-Maintained Horses",
      description:
        "Our horses are carefully selected and well-trained, ensuring a safe and enjoyable riding experience for riders of all ages and skill levels.",
    },
    {
      title: "Professional Staff",
      description:
        "Our experienced and friendly staff are passionate about horses and dedicated to providing excellent service and guidance to all our visitors.",
    },
    {
      title: "Family-Friendly",
      description:
        "We welcome families with children of all ages and provide activities suitable for everyone, from pony rides for toddlers to full trail rides for adults.",
    },
  ];

  const liveryServices = [
    {
      title: "Full Livery",
      description:
        "Complete care for your horse including feeding, turnout, mucking out, and daily exercise. Perfect for busy horse owners who want the best care for their equine companions.",
    },
  ];

  const lessonTypes = [
    {
      title: "Private Lessons",
      description:
        "One-on-one instruction tailored to your individual needs and goals. Perfect for beginners or advanced riders looking to improve specific skills.",
    },
    {
      title: "Group Lessons",
      description:
        "Learn alongside other riders in a supportive group environment. Great for building confidence and making new friends who share your passion for horses.",
    },
    {
      title: "Children's Lessons",
      description:
        "Specially designed lessons for young riders, focusing on safety, confidence, and developing a love for horses in a fun, educational environment.",
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About The Stables | Horse Riding Farm Pretoria East, Boschkop"
        description="Learn about The Stables — a family-friendly horse riding farm in Pretoria East, Boschkop. Expert guides, gentle horses, and unforgettable experiences for all ages."
        path="/about"
        image="https://www.thestableshorserides.co.za/aboutUs.jpg"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 via-stables-50 to-stables-100">
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            About The Stables
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            A Magical Horse Riding Experience for the Whole Family
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/horse-rides"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Rides
            </a>
            <a
              href="/contact"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/aboutUs.jpg')",
          }}
        ></div>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
      </section>

      {/* Main About Content */}
      <section className="py-20 bg-gradient-to-br from-cream-50 to-stables-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-stables-800 mb-6">
                Our Story
              </h2>
              <p className="text-lg text-stables-700 leading-relaxed mb-6">
                At The Stables, we offer a unique and magical horse riding
                experience for the whole family. Our rides are perfect for
                introducing young children to the wonderful world of horse
                riding. Our experienced and friendly staff are dedicated to
                ensuring that all our visitors have a fun and educational
                experience.
              </p>
              <p className="text-lg text-stables-700 leading-relaxed mb-6">
                Come and join us and make unforgettable memories with your loved
                ones. We believe that every child should have the opportunity to
                experience the joy and wonder of horseback riding in a safe,
                supportive environment.
              </p>
              <p className="text-lg text-stables-700 leading-relaxed">
                Our beautiful location in Pretoria East, Boschkop provides the
                perfect setting for your horse riding adventure, with scenic
                trails and peaceful surroundings that will make your experience
                truly special.
              </p>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
              <img
                src="/aboutUshorse.jpg"
                alt="Fields, Stable and horse paddocks"
                className="relative rounded-3xl shadow-2xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-300 w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-stables-800 mb-6">
              Our Mission & Values
            </h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              We are committed to providing safe, educational, and memorable
              horse riding experiences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {missionValues.map((value, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-1 sm:group-hover:rotate-2 transition-transform duration-300"></div>
                <div className="relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-stables-200 p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-stables-500 to-stables-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-stables-800 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-stables-700 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Facilities */}
      <section className="py-20 bg-gradient-to-br from-cream-50 to-stables-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
              <img
                src="/livery.jpg"
                alt="Our beautiful location"
                className="relative rounded-3xl shadow-2xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-300 w-full aspect-[4/3] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-stables-800 mb-8">
                Our Location & Facilities
              </h2>
              <div className="space-y-6">
                {facilities.map((facility, index) => (
                  <div key={index} className="group relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-2xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300 opacity-0 group-hover:opacity-10"></div>
                    <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-stables-200 p-6">
                      <h3 className="text-xl font-semibold text-stables-600 mb-3 group-hover:text-stables-700 transition-colors">
                        {facility.title}
                      </h3>
                      <p className="text-stables-700 leading-relaxed">
                        {facility.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Livery & Lessons */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-stables-800 mb-6">
              Livery & Lessons
            </h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              Beyond our family rides, we offer comprehensive livery services
              and professional riding lessons
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Livery Services */}
            <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-stables-100 p-8">
                <h3 className="text-3xl font-bold text-stables-800 mb-8">
                  Livery Services
                </h3>
                <div className="space-y-6">
                  {liveryServices.map((service, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-stables-500 pl-4"
                    >
                      <h4 className="text-xl font-semibold text-stables-600 mb-3">
                        {service.title}
                      </h4>
                      <p className="text-stables-700 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  ))}
                </div>
            </div>

            {/* Riding Lessons */}
            <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-stables-100 p-8">
                <h3 className="text-3xl font-bold text-stables-800 mb-8">
                  Riding Lessons
                </h3>
                <div className="space-y-6">
                  {lessonTypes.map((lesson, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-stables-500 pl-4"
                    >
                      <h4 className="text-xl font-semibold text-stables-600 mb-3">
                        {lesson.title}
                      </h4>
                      <p className="text-stables-700 leading-relaxed">
                        {lesson.description}
                      </p>
                    </div>
                  ))}
                </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-stables-200 p-8 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-stables-800 mb-4">
                Interested in Livery or Lessons?
              </h3>
              <p className="text-stables-700 mb-6 leading-relaxed">
                Contact us to discuss your specific needs and find the perfect
                livery option or lesson package for you and your horse.
              </p>
              <a
                href="mailto:info@thestables.co.za?subject=Livery%20and%20Lessons%20Inquiry"
                className="bg-gradient-to-r from-stables-600 to-stables-700 hover:from-stables-700 hover:to-stables-800 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all duration-300 transform hover:scale-105 inline-block"
              >
                Email Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-20 bg-gradient-to-r from-stables-600 to-stables-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 border-2 border-white rounded-full"></div>
              <div className="absolute top-8 right-8 w-16 h-16 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-8 left-8 w-24 h-24 border-2 border-white rounded-full"></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-6">Follow Our Adventures</h2>
              <p className="text-xl mb-8 text-stables-100">
                Stay connected with us on social media to see our latest photos,
                videos, and updates
              </p>
              <div className="flex justify-center gap-8">
                <a
                  href="https://www.facebook.com/thestableshorserides"
                  className="bg-white text-stables-800 hover:bg-cream-50 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 transform hover:scale-105"
                >
                  Facebook
                </a>
                <a
                  href="https://www.instagram.com/thestables.horserides/"
                  className="bg-white text-stables-800 hover:bg-cream-50 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 transform hover:scale-105"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
